/*
  OkosCucc cart page (loaded by sections/oc-main-cart.liquid). Contract: design/app_compatibility_rules.md §2.1 and
  design_files/cart/cart-custom-fields.md (the live cart-vat-2step, cart-agreement and cart-discount behaviour, unchanged):
  - checkout gate: #checkout aria-disabled="true" until every input[name="cart-agreement"] is checked and, while the company
    toggle .cart-vat-2step-checkbox is on, the VAT number (trimmed, upper-cased) matches ^([A-Z]{2}\d{8}|\d{8}-\d{1}-\d{2})$;
    also while a cart request runs. The submit guard cancels every submit while it is "true" (a11y #17: aria-disabled, not
    disabled, so the button stays in the Tab order). A checkout attempt marks unchecked agreements .is-unchecked.
  - company fields: cart ATTRIBUTES "Company Name" / "VAT Number" posted to /cart/update.js (company name on change and
    blur; VAT only when valid, on input and blur); unticking the toggle clears both values and posts them empty.
  - coupon: {discount: "A,B", sections: [this section]} to /cart/update.json; discount_codes[].applicable decides.
  - lines: OC.cartLines (assets/oc-cart-drawer.js) -> OC.cart.change; after every oc:cart:changed this section re-renders
    through the Section Rendering API. This file fires no oc:cart:changed of its own.
  - add-ons: sections/oc-cart-recs.liquid via routes.product_recommendations_url (intent=complementary), added with OC.cart.add.
*/
(function () {
  const VAT_REGEX = /^([A-Z]{2}\d{8}|\d{8}-\d{1}-\d{2})$/;

  function init() {
    const OC = (window.OC = window.OC || {});
    let root = document.querySelector('[data-oc-cart-page]');
    if (!root || !OC.cartLines) return;
    const wrapper = root.parentElement; // the shopify-section element: it stays, its content is swapped
    const sectionId = root.dataset.sectionId;
    const routes = OC.routes || {};
    const strings = () => OC.strings || {};
    let pageStrings = {};
    try {
      pageStrings = JSON.parse(document.getElementById('oc-cart-page-strings').textContent);
    } catch (e) {
      pageStrings = {};
    }
    let localBusy = false; // coupon request
    let renderSeq = 0;

    const lines = OC.cartLines.bind(wrapper, {
      onBusy(on) {
        root.classList.toggle('is-busy', on || localBusy);
        gate();
      },
      // A removed line: focus moves to the first remaining quantity field (or the back link on the empty page).
      focusFallback: () => root.querySelector('[data-lines] [data-qty]') || root.querySelector('.back'),
      // Status region outside root (a11y #21): "Kosár frissítve, részösszeg: …" after a successful line change.
      live: () => wrapper.querySelector('[data-cart-live]'),
    });
    const busy = () => lines.isBusy() || localBusy;

    // ---------- checkout gate ----------
    function gate() {
      const button = root.querySelector('#checkout');
      if (!button) return;
      const agreements = Array.from(document.querySelectorAll('input[name="cart-agreement"]'));
      const allAgreed = agreements.length === 0 || agreements.every((box) => box.checked);
      let vatOk = true;
      root.querySelectorAll('.cart-vat-2step').forEach((block) => {
        const toggle = block.querySelector('.cart-vat-2step-checkbox');
        const vat = block.querySelector('.cart-vat-2step-input');
        if (toggle && toggle.checked) {
          const value = vat ? vat.value.trim().toUpperCase() : '';
          if (!(value !== '' && VAT_REGEX.test(value))) vatOk = false;
        }
      });
      let reason = '';
      if (!vatOk) reason = pageStrings.reasonVat || '';
      else if (!allAgreed) reason = pageStrings.reasonAgreements || '';
      // aria-disabled instead of disabled (a11y #17, 29 Oct 2026): still focusable; the submit guard below blocks it.
      const blocked = !vatOk || !allAgreed || root.dataset.empty === 'true' || busy();
      button.setAttribute('aria-disabled', String(blocked));
      // The markup's native disabled only covers the time before this script (no guard yet); from here the guard decides.
      button.disabled = false;
      // Waiting for the tickboxes / VAT (round 4): orchid outline + purple glow on #checkout and the bar button (CSS [data-wait]);
      // not while a request is pending and not with an empty cart.
      const wait = !!reason && !busy() && root.dataset.empty !== 'true';
      button.toggleAttribute('data-wait', wait);
      const barButton = root.querySelector('[data-cbar-btn]');
      if (barButton) barButton.toggleAttribute('data-wait', wait);
      const why = root.querySelector('#checkout-reason');
      if (why) {
        why.hidden = !reason;
        why.querySelector('span').textContent = reason;
      }
      barSync(button, allAgreed);
    }

    // ---------- sticky checkout bar (design round 3 §5; contract §0.9 / §2.1 v3.15) ----------
    // GATE: the bar adds no condition of its own and never weakens the one above. Its button is the same submit as #checkout
    // (type="submit" form="cart" name="checkout") and mirrors #checkout's aria-disabled (still clickable). While
    // #checkout is blocked a click is cancelled and, unless a request runs or the cart is empty, toGate() scrolls to the
    // first unmet requirement. Ready: the native submit goes through untouched, exactly like #checkout.
    // The bar lives inside root and is not a data-part: partial re-renders keep it (only its total, data-part "bar-total",
    // is swapped); the empty <-> filled swap replaces root and observeBar() re-binds the observer to the new #checkout.
    const reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    let checkoutInView = true; // stays true without IntersectionObserver: the bar then simply never shows
    let footerInView = false;
    let checkoutObserver = null;
    function barShow() {
      const bar = root.querySelector('[data-cbar]');
      if (!bar) return;
      const show = !checkoutInView && !footerInView && root.dataset.empty !== 'true';
      if (bar.hasAttribute('data-show') === show && bar.inert === !show) return;
      if (!show && bar.contains(document.activeElement)) {
        // Focus must not stay inside an inert bar: hand it to #checkout (or, while that is blocked, the first agreement).
        const button = root.querySelector('#checkout');
        const fallback = button && !checkoutBlocked() ? button : document.querySelector('input[name="cart-agreement"]');
        if (fallback) fallback.focus({ preventScroll: true });
      }
      bar.toggleAttribute('data-show', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.inert = !show;
    }
    function barSync(button, allAgreed) {
      const bar = root.querySelector('[data-cbar]');
      if (!bar) return;
      const barButton = bar.querySelector('[data-cbar-btn]');
      if (barButton) barButton.setAttribute('aria-disabled', String(checkoutBlocked()));
      const link = bar.querySelector('[data-cbar-agr]');
      if (link) {
        const done = allAgreed && document.querySelectorAll('input[name="cart-agreement"]').length > 0;
        link.classList.toggle('is-done', done);
        const use = link.querySelector('use');
        if (use) use.setAttribute('href', done ? '#i-check' : '#i-alert');
        const label = done ? pageStrings.barAgreementsDone : pageStrings.barAgreements;
        if (label) link.querySelector('span').textContent = label;
      }
      barShow();
    }
    function checkoutBlocked() {
      const button = root.querySelector('#checkout');
      return !button || button.getAttribute('aria-disabled') === 'true';
    }
    // An unticked agreement after a blocked attempt: .is-unchecked (glow, red text) plus, not by colour alone, aria-invalid
    // and the #checkout-reason text as its description (a11y #13, 29 Oct 2026). unmarkBox() undoes both once it is ticked.
    function markBox(box) {
      const row = box.closest('.chk');
      if (row) row.classList.add('is-unchecked');
      box.setAttribute('aria-invalid', 'true');
      box.setAttribute('aria-describedby', 'checkout-reason');
    }
    function unmarkBox(box) {
      const row = box.closest('.chk');
      if (row) row.classList.remove('is-unchecked');
      box.removeAttribute('aria-invalid');
      box.removeAttribute('aria-describedby');
    }
    // Scroll to and focus the first unmet requirement: every unticked agreement is marked (markBox, the same orchid glow as a
    // press on the blocked #checkout) and the first one is focused; all ticked but the VAT number invalid: the VAT field.
    function toGate() {
      const boxes = Array.from(document.querySelectorAll('input[name="cart-agreement"]'));
      const open = boxes.filter((box) => !box.checked);
      open.forEach(markBox);
      let target = open[0];
      if (!target) {
        root.querySelectorAll('.cart-vat-2step').forEach((block) => {
          const p = vatParts(block);
          if (target || !p.toggle || !p.toggle.checked || !p.vat) return;
          const value = p.vat.value.trim().toUpperCase();
          if (value !== '' && VAT_REGEX.test(value)) return;
          target = p.vat;
          if (value !== '') validateVat(block, true);
        });
      }
      target = target || boxes[0] || root.querySelector('#cart-agreements');
      if (!target) return;
      target.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      target.focus({ preventScroll: true });
    }
    // Observe the CURRENT #checkout (re-called after root is replaced) and, once, the site footer.
    function observeBar() {
      if (!('IntersectionObserver' in window)) return;
      if (checkoutObserver) checkoutObserver.disconnect();
      checkoutObserver = null;
      checkoutInView = true;
      const button = root.querySelector('#checkout');
      if (button && root.querySelector('[data-cbar]')) {
        const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--oc3-header-h')) || 0;
        checkoutObserver = new IntersectionObserver(
          (entries) => {
            checkoutInView = entries[entries.length - 1].isIntersecting;
            barShow();
          },
          { rootMargin: `-${headerH}px 0px 0px 0px` }
        );
        checkoutObserver.observe(button);
      }
      barShow();
    }
    const footer = document.querySelector('footer.ft') || document.querySelector('footer');
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        footerInView = entries[entries.length - 1].isIntersecting;
        barShow();
      }).observe(footer);
    }

    // ---------- company purchase (live cart-vat-2step) ----------
    function vatParts(block) {
      return {
        toggle: block.querySelector('.cart-vat-2step-checkbox'),
        wrap: block.querySelector('.cart-vat-2step__input-wrapper'),
        company: block.querySelector('.cart-vat-2step-company-input'),
        vat: block.querySelector('.cart-vat-2step-input'),
        error: block.querySelector('.cart-vat-2step__error-message'),
        success: block.querySelector('.cart-vat-2step__success-message'),
      };
    }
    // Attribute posts run one after another, so a blur save can never land after the toggle-off clear.
    let attributeQueue = Promise.resolve();
    function postAttributes(attributes) {
      attributeQueue = attributeQueue
        .then(() =>
          fetch(`${routes.cartUpdate || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes }),
          })
        )
        .catch(() => {});
    }
    function saveAttributes(block) {
      const p = vatParts(block);
      postAttributes({ 'Company Name': p.company.value.trim(), 'VAT Number': p.vat.value.trim().toUpperCase() });
    }
    // Shows the error, the success or no message, and points aria-describedby at the hint plus ONLY the message on screen
    // (a11y #2, 29 Oct 2026: describedby also reads display:none targets, so both texts were always announced).
    function vatMessage(p, kind) {
      p.error.classList.toggle('show', kind === 'error');
      p.success.classList.toggle('show', kind === 'ok');
      const ids = [`vat-hint-${p.vat.dataset.vatBlockId}`];
      if (kind === 'error') ids.push(p.error.id);
      else if (kind === 'ok') ids.push(p.success.id);
      p.vat.setAttribute('aria-describedby', ids.join(' '));
    }
    function validateVat(block, showMessages) {
      const p = vatParts(block);
      const value = p.vat.value.trim().toUpperCase();
      if (value === '') {
        if (showMessages) vatMessage(p, '');
        p.vat.classList.remove('invalid', 'valid');
        p.vat.removeAttribute('aria-invalid');
        gate();
        return false;
      }
      const valid = VAT_REGEX.test(value);
      p.vat.classList.toggle('valid', valid);
      p.vat.classList.toggle('invalid', !valid);
      if (valid) p.vat.removeAttribute('aria-invalid');
      else p.vat.setAttribute('aria-invalid', 'true');
      if (showMessages) vatMessage(p, valid ? 'ok' : 'error');
      if (valid) saveAttributes(block);
      gate();
      return valid;
    }
    function toggleCompany(block) {
      const p = vatParts(block);
      const on = p.toggle.checked;
      p.wrap.classList.toggle('active', on);
      p.toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (on) {
        p.company.focus();
      } else {
        p.company.value = '';
        p.vat.value = '';
        p.vat.classList.remove('invalid', 'valid');
        p.vat.removeAttribute('aria-invalid');
        vatMessage(p, '');
        postAttributes({ 'Company Name': '', 'VAT Number': '' });
      }
      gate();
    }
    // A cart that already has either attribute starts with the toggle on and open (live behaviour).
    function initCompany() {
      root.querySelectorAll('.cart-vat-2step').forEach((block) => {
        const p = vatParts(block);
        if (!p.toggle || !p.company || !p.vat) return;
        if (p.company.value.trim() !== '' || p.vat.value.trim() !== '') {
          p.toggle.checked = true;
          p.wrap.classList.add('active');
          p.toggle.setAttribute('aria-expanded', 'true');
          if (p.vat.value.trim() !== '') validateVat(block, false);
        }
      });
      gate();
    }

    wrapper.addEventListener('change', (event) => {
      const target = event.target;
      const block = target.closest && target.closest('.cart-vat-2step');
      if (target.matches('.cart-vat-2step-checkbox') && block) toggleCompany(block);
      else if (target.matches('.cart-vat-2step-company-input') && block) saveAttributes(block);
      else if (target.name === 'cart-agreement') {
        if (target.checked) unmarkBox(target);
        else {
          const row = target.closest('.chk');
          if (row) row.classList.remove('is-unchecked');
        }
        gate();
      }
    });
    wrapper.addEventListener('input', (event) => {
      const block = event.target.closest && event.target.closest('.cart-vat-2step');
      if (event.target.matches('.cart-vat-2step-input') && block && validateVat(block, false)) {
        // A valid number shows its confirmation while typing: if it appeared only on blur, it would push "Tovább a
        // pénztárhoz" down at the moment of the tap that closes the phone keyboard, and that first tap would miss
        // (owner, 28 Oct 2026). The error message still waits for blur.
        vatMessage(vatParts(block), 'ok');
        return;
      }
      // A wrong coupon stays on screen until the code is edited or a new one is sent (a11y #16, 29 Oct 2026; no timeout).
      const disc = event.target.matches('input[name="discount"]') && event.target.closest('[data-disc]');
      if (disc && disc.querySelector('[data-disc-err].show')) couponMessage(disc, '');
    });
    wrapper.addEventListener('focusout', (event) => {
      const block = event.target.closest && event.target.closest('.cart-vat-2step');
      if (!block) return;
      if (event.target.matches('.cart-vat-2step-company-input')) saveAttributes(block);
      else if (event.target.matches('.cart-vat-2step-input')) validateVat(block, true);
    });
    wrapper.addEventListener('focusin', (event) => {
      const block = event.target.closest && event.target.closest('.cart-vat-2step');
      if (block && event.target.matches('.cart-vat-2step-input')) vatMessage(vatParts(block), '');
    });

    // Checkout attempt (mousedown / pointerdown, as live): every unchecked agreement turns .is-unchecked.
    // Listened on the button's wrapper (kept from when #checkout was natively disabled, before a11y #17).
    function markUnchecked(event) {
      if (!event.target.closest || !event.target.closest('[data-checkout-hit]')) return;
      document.querySelectorAll('input[name="cart-agreement"]').forEach((box) => {
        if (!box.checked) markBox(box);
      });
    }
    wrapper.addEventListener('mousedown', markUnchecked);
    wrapper.addEventListener('pointerdown', markUnchecked);

    // ---------- re-render (Section Rendering API) ----------
    function swap(html) {
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-oc-cart-page]');
      if (!fresh) throw new Error('cart section markup missing');
      lines.beforeRender();
      if (fresh.dataset.empty !== root.dataset.empty) {
        // Empty <-> filled: the whole page content changes.
        root.replaceWith(fresh);
        root = fresh;
        initCompany();
        observeBar();
      } else {
        fresh.querySelectorAll('[data-part]').forEach((node) => {
          const old = root.querySelector(`[data-part="${node.dataset.part}"]`);
          if (old) old.replaceWith(node);
        });
        root.dataset.firstProduct = fresh.dataset.firstProduct;
      }
    }

    async function render() {
      const seq = ++renderSeq;
      lines.rendering += 1;
      lines.apply();
      try {
        const response = await fetch(`${routes.cart || '/cart'}?section_id=${encodeURIComponent(sectionId)}`);
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        if (seq !== renderSeq) {
          // a newer render is on its way: release this one's busy count
          lines.rendering -= 1;
          lines.apply();
          return;
        }
        swap(html);
        lines.rendering -= 1;
        lines.afterRender();
        gate();
        loadRecs();
        return;
      } catch (error) {
        if (seq === renderSeq) lines.setError(strings().cartError);
      }
      lines.rendering -= 1;
      lines.apply();
    }
    document.addEventListener('oc:cart:changed', render);

    // ---------- coupon (live cart-discount) ----------
    function appliedCodes() {
      return Array.from(root.querySelectorAll('[data-code]')).map((item) => item.dataset.code);
    }
    async function postCodes(codes) {
      localBusy = true;
      lines.rendering += 1;
      lines.apply();
      lines.setError('');
      try {
        const response = await fetch(`${routes.cartUpdate || '/cart/update'}.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections: [sectionId] }),
        });
        if (!response.ok) throw new Error(String(response.status));
        return await response.json();
      } finally {
        localBusy = false;
        lines.rendering -= 1;
      }
    }
    function applySection(data) {
      if (data && data.sections && data.sections[sectionId]) swap(data.sections[sectionId]);
      lines.afterRender();
      gate();
      // The drawer's totals changed too (no oc:cart:changed: the item count did not change).
      if (OC.cartDrawer) OC.cartDrawer.refresh();
    }
    function couponMessage(block, kind, text) {
      const error = block.querySelector('[data-disc-err]');
      const ok = block.querySelector('[data-disc-ok]');
      const input = block.querySelector('input[name="discount"]');
      error.classList.remove('show');
      ok.classList.remove('show');
      input.classList.remove('invalid');
      input.removeAttribute('aria-invalid');
      // aria-describedby names only the message on screen (a11y #2); the error stays until edited or resent (a11y #16).
      input.removeAttribute('aria-describedby');
      if (kind === 'error') {
        error.querySelector('span').textContent = text;
        error.classList.add('show');
        input.classList.add('invalid');
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', error.id);
      } else if (kind === 'ok') {
        ok.classList.add('show');
        input.setAttribute('aria-describedby', ok.id);
      }
    }
    async function applyCoupon(block) {
      const input = block.querySelector('input[name="discount"]');
      const button = block.querySelector('[data-disc-form] [type="submit"]');
      const code = input.value.trim();
      couponMessage(block, '');
      if (!code || busy()) return;
      const current = appliedCodes();
      if (current.some((c) => c.toLowerCase() === code.toLowerCase())) {
        couponMessage(block, 'error', block.dataset.already);
        return;
      }
      const label = button.textContent;
      button.disabled = true;
      button.textContent = block.dataset.loading;
      try {
        const data = await postCodes([...current, code]);
        const entry = (data.discount_codes || []).find((d) => String(d.code).toLowerCase() === code.toLowerCase());
        if (!entry || entry.applicable === false) {
          input.value = '';
          throw new Error('not applicable');
        }
        applySection(data);
        input.value = '';
        couponMessage(block, 'ok');
        button.textContent = block.dataset.success;
        setTimeout(() => {
          button.textContent = label;
          button.disabled = false;
        }, 2000);
      } catch (error) {
        lines.apply();
        gate();
        couponMessage(block, 'error', block.dataset.error);
        button.textContent = label;
        button.disabled = false;
      }
    }
    async function removeCoupon(code) {
      if (busy()) return;
      try {
        const data = await postCodes(appliedCodes().filter((c) => c !== code));
        applySection(data);
      } catch (error) {
        lines.apply();
        gate();
        lines.setError(strings().cartError);
      }
    }
    wrapper.addEventListener('submit', (event) => {
      // Guard (contract §2.1): a submit of form#cart while #checkout is aria-disabled never goes through (#checkout itself, bar,
      // Enter key, anything). Since a11y #17 this guard is what stops a press on #checkout; that press then runs toGate().
      if (event.target.id === 'cart') {
        if (!checkoutBlocked()) return;
        event.preventDefault();
        const submitter = event.submitter;
        if ((!submitter || submitter.name === 'checkout') && !busy() && root.dataset.empty !== 'true') toGate();
        return;
      }
      const form = event.target.closest && event.target.closest('[data-disc-form]');
      if (!form) return;
      event.preventDefault();
      applyCoupon(form.closest('[data-disc]'));
    });

    // ---------- add-ons ----------
    async function loadRecs() {
      const box = root.querySelector('[data-recs]');
      if (!box) return;
      const productId = root.dataset.firstProduct;
      if (!productId || !routes.productRecommendations) {
        box.hidden = true;
        return;
      }
      try {
        const url = `${routes.productRecommendations}?section_id=oc-cart-recs&product_id=${encodeURIComponent(productId)}&intent=complementary&limit=4`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));
        const list = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-rec-list]');
        const rows = list ? list.innerHTML.trim() : '';
        box.querySelector('[data-rec-list]').innerHTML = rows;
        box.hidden = !list || !list.querySelector('[data-rec]');
        lines.apply();
      } catch (error) {
        box.hidden = true;
      }
    }

    wrapper.addEventListener('click', (event) => {
      // Sticky bar (delegated, so it survives every re-render): the link always goes to the agreements; the button submits
      // only while #checkout is enabled, otherwise the click is cancelled and toGate() shows what is missing.
      if (event.target.closest('[data-cbar-agr]')) {
        event.preventDefault();
        toGate();
        return;
      }
      if (event.target.closest('[data-cbar-btn]')) {
        if (checkoutBlocked()) {
          event.preventDefault();
          if (!busy() && root.dataset.empty !== 'true') toGate();
        }
        return;
      }
      const uncode = event.target.closest('[data-uncode]');
      if (uncode) {
        event.preventDefault();
        removeCoupon(uncode.closest('[data-code]').dataset.code);
        return;
      }
      const add = event.target.closest('[data-addrec]');
      if (!add || busy() || !OC.cart) return;
      const rec = add.closest('[data-rec]');
      const old = rec && rec.querySelector('.rec__err');
      if (old) old.remove();
      // Success: OC.cart refreshes (one oc:cart:changed) and opens the drawer with "Betettük a kosárba".
      OC.cart.add({ id: Number(add.dataset.addrec), quantity: 1 }).catch(() => {
        if (rec && document.contains(rec)) {
          rec.insertAdjacentHTML(
            'beforeend',
            '<p class="rec__err" role="alert"><svg class="ico" aria-hidden="true"><use href="#i-alert"/></svg><span></span></p>'
          );
          rec.querySelector('.rec__err span').textContent = pageStrings.addError || '';
        }
      });
    });

    initCompany();
    observeBar();
    loadRecs();
  }

  // oc-cart-drawer.js (OC.cartLines) is loaded later in the page (the drawer section comes after content_for_layout).
  // Deferred scripts run while readyState is already "interactive", so wait for DOMContentLoaded unless OC.cartLines exists.
  if (window.OC && window.OC.cartLines) init();
  else document.addEventListener('DOMContentLoaded', init, { once: true });
})();
